import React, { createRef, useEffect, useRef, useState } from "react";
import {
  SafeAreaView,
  View,
  StyleSheet,
  ScrollView,
  InputAccessoryView,
  Keyboard,
  TouchableOpacity,
  Platform,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useNavigation } from "@react-navigation/native";
import { Grid, Container, Row, Column } from "ui-grid";
import {
  typography,
  Text,
  theme,
  Icons,
  Button,
  ButtonType,
  ErrorWarningBottomSheet,
} from "ui-components";
import {
  getPropertyFees,
  setDefaultPaymentSourceUpdated,
} from "../../redux/actions/actionCreators";
import { fontWeights } from "ui-components/dist/theme/typography";
import { RootState } from "../../redux/reducers";
import { translate } from "../../i18n";
import { NumericInputNew } from "../../components";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import automationTestingLocators from "./utils/automation-testing-locators.json";

import { DatePickerDropdownNew } from "../../components/new/dropdown/new-date-picker-dropdown";
import moment from "moment";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { enterPaymentDetailsSchema } from "./utils/validation-schemas-payment";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { startOfDay, add } from "date-fns";

import Big from "big.js";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import { toAccountBalanceString } from "../../utils/money";
import { PaymentFlowProgressIndicator } from "../../components/new/payment-flow/payment-flow-progress-indicator";
import Auryc from "rn-auryc-sdk";

const MAXIMUM_PAYMENT_MONTHS_AHEAD = 3;

export type Props = HomeScreenProps;
export const PaymentFlowPaymentDetails = ({ route }: Props) => {
  const logger = useSelector((state: RootState) => state.logger);
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);

  const ref = [createRef(), createRef()];
  const refPopupWarning = useRef();

  const { params } = route;
  const {
    propertyUnit,
    propName,
    balanceDue = 0,
    currentPropertyProfile,
    status,
    currentPropertyFeeProfile,
    isAimPastResident,
  } = params;
  const prePaymentSupported = true;
  let accountBalance = toAccountBalanceString(balanceDue);
  const balanceWithCents = balanceDue ? (isNaN(balanceDue) ? 0 : (balanceDue / 100).toFixed(2)) : 0;
  const finalBalance = balanceWithCents > 0 ? balanceWithCents : 0;
  let disableBothInput = prePaymentSupported ? false : balanceDue <= 0;

  const { allow_partial_rent_payments: allowPartialRentPayments } = currentPropertyProfile;
  const [amount, setAmount] = useState(isAimPastResident ? 0 : finalBalance);

  const start = moment.utc();
  const [date, setDate] = useState(new Date(start));
  const minimumDate = startOfDay(new Date());
  const maximumDate = add(new Date(), { months: MAXIMUM_PAYMENT_MONTHS_AHEAD });

  const inputAccessoryViewID = "inputAccessoryViewID1";
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  let defaultValues: any = {
    paymentAmount: amount,
    paymentDate: date,
  };

  const {
    register,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      enterPaymentDetailsSchema({
        allowPartialRentPayments,
        balanceDue: finalBalance,
        currentPropertyFeeProfile,
      })
    ),
    defaultValues,
  });

  const fetchPropertyFees = async (amount: number, propertyId: string | number) => {
    await dispatch(getPropertyFees({ amount }, propertyId));
  };

  const getPropertyFeesOnBlur = () => {
    // get property fees
    const propertyId = params.currentProperty.id;
    if (propertyId) {
      fetchPropertyFees(getAmounts()?.amount, propertyId);
    }
  };

  useEffect(() => {
    register("paymentAmount");
    register("paymentDate");
  }, [register]);

  const getAmounts = () => {
    let paymentAmount = getValues("paymentAmount");
    if (!paymentAmount) return {};
    paymentAmount = Big(paymentAmount).mul(100);
    const amounts = {
      amount: paymentAmount,
      // These are determined later
      platformFee: Big(0),
      total: paymentAmount,
    };
    return amounts;
  };

  const getPaymentDate = () => {
    const paymentDate = getValues("paymentDate");
    return paymentDate;
  };

  const onChange = (selectedDate: any) => {
    const currentDate = selectedDate || date;
    clearErrors("paymentDate");
    setValue("paymentDate", currentDate);
    setDate(currentDate);
  };

  const onContinue = async () => {
    getPropertyFeesOnBlur();
    const isValid = await trigger("paymentAmount", "paymentDate");
    if (!isValid) {
      refPopupWarning?.current?.open();
    }
    if (isValid) {
      dispatch(setDefaultPaymentSourceUpdated(null));
      navigation.navigate("PaymentFlowPaymentMethod", {
        finalPaymentAmounts: getAmounts(),
        finalPaymentDate: getPaymentDate(),
        ...params,
      });
    }
  };
  return (
    <>
      <SafeAreaView style={styles.container}>
        <View style={{ marginVertical: 20 }}>
          <PaymentFlowProgressIndicator currentPosition={0} />
        </View>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollableArea}>
          <View>
            <Grid>
              <Container>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column style={[styles.paymentDueCard]} span={{ xs: 12, sm: 8, md: 8, lg: 8 }}>
                    <View style={styles.paymentDetailsTextView}>
                      <Text
                        style={styles.paymentDetailsLeftText}
                        {...automationTestingLocators.paymentFlowPaymentDetails
                          .userProperty}>{`${propName} ${
                        propertyUnit ? ", #" + propertyUnit : ""
                      }`}</Text>
                    </View>
                    <View style={styles.paymentDetailsTextView}>
                      <Text
                        style={styles.paymentDetailsLeftText}
                        {...automationTestingLocators.paymentFlowPaymentDetails.userPaymentDueText}>
                        {translate("paymentFlow.details.accountBalance")}
                      </Text>
                      {status === "PENDING" ? (
                        <Text
                          style={styles.notAvailableText}
                          {...automationTestingLocators.paymentFlowPaymentDetails.userPaymentDue}>
                          {translate("paymentFlow.details.notAvailable")}
                        </Text>
                      ) : (
                        <Text
                          style={styles.paymentDetailsRightText}
                          {...automationTestingLocators.paymentFlowPaymentDetails.userPaymentDue}>
                          {isAimPastResident
                            ? translate("paymentFlow.details.contactPropertyForBalance")
                            : accountBalance}
                        </Text>
                      )}
                    </View>

                    <View style={{ margin: 10 }} />
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>

                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View style={styles.inputRowInner}>
                      <NumericInputNew
                        labelStyle={{ fontFamily: "Lato_700Bold" }}
                        label={translate("paymentFlow.details.enterAmount") as string}
                        name="paymentAmount"
                        value={amount}
                        onChangeText={(val) => {
                          clearErrors("paymentAmount");
                          setAmount(val);
                          setValue("paymentAmount", val);
                        }}
                        error={errors.paymentAmount}
                        inputAccessoryViewID={inputAccessoryViewID}
                        ref={ref[0] as any}
                        onFocus={() => setCurrentInputIndex(0)}
                        errors={errors?.["paymentAmount"]}
                        placeholder={"0.00"}
                        leftIcon={Icons["dollar"]}
                        leftIconColor={theme.colors.primary}
                        onSubmitEditing={() => {}}
                        disabled={disableBothInput}
                        {...automationTestingLocators.paymentFlowPaymentDetails.paymentAmount}
                      />
                      <View style={{ margin: 5 }} />
                    </View>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View
                      style={styles.inputRowInner}
                      ref={(x) => Auryc.unMarkViewAsSensitiveInformation(x)}>
                      <DatePickerDropdownNew
                        date={date}
                        ref={ref[1 as any]}
                        minimumDate={minimumDate}
                        maximumDate={maximumDate}
                        onChange={onChange}
                        title={translate("paymentFlow.details.selectPaymentDate") as string}
                        {...automationTestingLocators.paymentFlowPaymentDetails.paymentDatePicker}
                        disabled={disableBothInput}
                      />
                    </View>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
              </Container>
            </Grid>
          </View>
        </ScrollView>

        <BottomNav backgroundColor={theme.colors.white}>
          <View style={{ marginTop: 15 }} />
          <Row>
            <Column span={{ xs: false, sm: 3, md: 3, lg: 3 }} />
            <Column span={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <Button
                text={translate("paymentFlow.details.next") as string}
                onPress={() => {
                  onContinue();
                }}
                style={{
                  backgroundColor: amount == 0 ? theme.colors.text2 : theme.colors.primary,
                }}
                type={ButtonType.primary}
                disabled={disableBothInput}
                {...automationTestingLocators.paymentFlowPaymentDetails.paymentFlowNextButton}
              />
            </Column>
            <Column span={{ xs: false, sm: 3, md: 3, lg: 3 }} />
          </Row>
          <View style={{ marginTop: 25 }}></View>
        </BottomNav>
        <Footer />
        <ErrorWarningBottomSheet
          ref={refPopupWarning}
          height={190}
          title={translate("paymentFlow.details.invalidAmount") as string}
          body={translate("paymentFlow.details.enterValidAmount") as string}
          alertType={"error"}
          iconName={"warning"}
          showButtons={true}
          showLeftButton={false}
          rightButtonText={translate("paymentFlow.details.gotIt") as string}
          onPressRightButton={() => refPopupWarning?.current?.close()}
        />
      </SafeAreaView>
      {Platform.OS == "ios" ? (
        <InputAccessoryView nativeID={inputAccessoryViewID} backgroundColor={"white"}>
          <TouchableOpacity onPress={() => Keyboard.dismiss()} style={styles.closeKeyboardButton}>
            <Text style={styles.closeKeyboardButtonText}>Done</Text>
          </TouchableOpacity>
        </InputAccessoryView>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: "#FFFFFF",
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: "#FFFFFF",
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: "#FFFFFF",
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollView: {
    flexGrow: 0,
  },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
    }),
  },
  paymentDueCard: {
    paddingVertical: 8,
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FAFAFA" },
      webTablet: {
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: { backgroundColor: "#FAFAFA" },
      iosTablet: {
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: { backgroundColor: "#FAFAFA" },
      androidTablet: {
        backgroundColor: "#FAFAFA",
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  paymentDetailsTextView: {
    flexDirection: "row",
    paddingVertical: 4,
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: { paddingLeft: 30, paddingRight: 30 },
      androidPhone: {},
      androidTablet: { paddingLeft: 30, paddingRight: 30 },
    }),
  },
  paymentDetailsLeftText: {
    ...typography.title2,
    color: theme.colors.text3,
    fontWeight: fontWeights.Regular,
    flex: 1,
  },
  paymentDetailsRightText: {
    ...typography.title2,
    color: theme.colors.secondary,
    fontWeight: fontWeights.Bold,
    fontFamily: "Lato_700Bold",
    textAlign: "right",
    marginLeft: 10,
  },
  notAvailableText: {
    ...typography.title2,
    color: theme.colors.text3,
    fontWeight: fontWeights.Regular,
    marginLeft: 10,
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
    }),
  },
  closeKeyboardButton: {
    textAlign: "right",
    backgroundColor: "#FFFFFF",
    paddingTop: 15,
    paddingBottom: 15,
    paddingRight: 15,
  },
  closeKeyboardButtonText: {
    textAlign: "right",
    fontSize: 18,
    fontWeight: "500",
    color: "#2196F3",
  },
});

export default PaymentFlowPaymentDetails;
